import React, { useEffect, useMemo, useState, type MouseEvent } from 'react';
import {
  Dialog,
  DialogActions,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { clamp } from 'lodash';
import { useTranslation } from '../../../contexts/TranslationContext';
import { MHMDialog } from '../MHM/MHMDialog';
import { device_controller } from '../../../controllers/device_controller';
import { MHMLoadingIcon } from '../MHM/MHMLoadingIcon';
import { MHMButton } from '../MHM/MHMButton';
import { DownloadCSVModalSelectDevices } from './DownloadCSVModalSelectDevices';
import { DownloadCSVModalOptions, file_types } from './DownloadCSVModalOptions';
import { useDownloadDeviceData } from '../../../hooks/useDownloadDeviceData';
import { DownloadCSVModalDates } from './DownloadCSVModalDates';
import { formatDeviceKey, parseDeviceKey } from '../../../utils/deviceKey';
import { selectedDeviceStateAtom } from '../../../models/atoms/device_atoms';
import { allDevicesMapSelector } from '../../../models/selectors/device_selectors';
import { useRecoilValue } from 'recoil';

type DownloadCSVModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  initial_start_date: Date;
  initial_end_date: Date;
  show_rainfall: boolean;
  show_water_level: boolean;
  show_filtered_water_level: boolean;
};

const DownloadCSVModal: React.FC<DownloadCSVModalProps> = (
  props: DownloadCSVModalProps,
) => {
  const { t, language } = useTranslation();
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const allDevicesMap = useRecoilValue(allDevicesMapSelector);
  const {
    selectedDeviceKeys,
    setSelectedDeviceKeys,
    mode,
    changeMode,
    minStartDate,
    maxEndDate,
  } = useDownloadDeviceData();
  const [startDate, setStartDate] = useState<Date>(
    new Date(
      clamp(
        props.initial_start_date.getTime(),
        minStartDate.getTime(),
        maxEndDate.getTime(),
      ),
    ),
  );
  const [endDate, setEndDate] = useState<Date>(
    new Date(
      clamp(
        props.initial_end_date.getTime(),
        minStartDate.getTime(),
        maxEndDate.getTime(),
      ),
    ),
  );
  const [showRainfall, setShowRainfall] = useState<boolean>(
    props.show_rainfall,
  );
  const [showWaterLevel, setShowWaterLevel] = useState<boolean>(
    props.show_water_level,
  );
  const [showFilteredWaterLevel, setShowFilteredWaterLevel] = useState<boolean>(
    props.show_filtered_water_level,
  );
  const [fileType, setFileType] = useState<string>('csv');
  const [loading, setLoading] = useState<boolean>(false);

  function closeModal() {
    props.setOpen(false);
  }

  async function handleDownload() {
    if (!isValid) {
      return;
    }
    setLoading(true);
    await device_controller.downloadCSV(
      selectedDeviceKeys.map(key => {
        const parsed = parseDeviceKey(key);
        const device = allDevicesMap.get(key);
        return {
          ...parsed,
          client_identifier: device?.client_identifier || null,
        };
      }),
      startDate.getTime(),
      endDate.getTime(),
      showRainfall,
      showWaterLevel,
      showFilteredWaterLevel,
      fileType,
      language,
      -new Date().getTimezoneOffset(),
    );
    setLoading(false);
    props.setOpen(false);
  }

  function handleChangeMode(
    _event: MouseEvent<HTMLElement>,
    newMode: 'live' | 'all' | 'archived' | null,
  ) {
    if (newMode === null) {
      return;
    }

    changeMode(newMode);
  }

  useEffect(() => {
    if (!selectedDevice) {
      return;
    }

    setSelectedDeviceKeys([
      formatDeviceKey(selectedDevice.device_id, selectedDevice.session_id),
    ]);
    setShowRainfall(props.show_rainfall);
    setShowWaterLevel(props.show_water_level);
    setShowFilteredWaterLevel(props.show_filtered_water_level);
    changeMode(selectedDevice.session_id ? 'all' : 'live');
  }, [
    selectedDevice,
    props.show_rainfall,
    props.show_water_level,
    props.show_filtered_water_level,
    setSelectedDeviceKeys,
    setShowFilteredWaterLevel,
    setShowRainfall,
    setShowWaterLevel,
  ]);

  // Ensures the download range covers the complete span of all selected devices. Reverts to the dashboard zoom level if the selection is cleared to prevent invalid date states.
  useEffect(() => {
    if (selectedDeviceKeys.length === 0) {
      setStartDate(props.initial_start_date);
      setEndDate(props.initial_end_date);
    } else {
      setStartDate(minStartDate);
      setEndDate(maxEndDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceKeys.length, minStartDate.getTime(), maxEndDate.getTime()]);

  const isValid = useMemo(() => {
    if (selectedDeviceKeys.length === 0) {
      return false;
    }

    if (startDate < minStartDate || startDate > endDate) {
      return false;
    }
    if (endDate > maxEndDate || endDate < startDate) {
      return false;
    }
    if (!file_types.includes(fileType)) {
      return false;
    }
    return true;
  }, [
    endDate,
    fileType,
    maxEndDate,
    minStartDate,
    selectedDeviceKeys,
    startDate,
  ]);

  return (
    <Dialog open={props.open} onClose={closeModal} fullWidth maxWidth="sm">
      <MHMDialog.Header onClose={closeModal}>
        {t('Download Data')}
      </MHMDialog.Header>

      <MHMDialog.Content direction={{ sm: 'column', md: 'row' }} gap={2}>
        <Stack flex={1} width={{ xs: 'auto', md: 0 }} rowGap={1}>
          <ToggleButtonGroup
            value={mode}
            color="primary"
            exclusive
            onChange={handleChangeMode}
          >
            <ToggleButton value="live">{t('Live')}</ToggleButton>
            <ToggleButton value="archived">{t('Archived')}</ToggleButton>
            <ToggleButton value="all">{t('All')}</ToggleButton>
          </ToggleButtonGroup>
          <DownloadCSVModalSelectDevices />
          <DownloadCSVModalDates
            minStartDate={minStartDate}
            maxEndDate={maxEndDate}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </Stack>
        <Stack flex={1} width={{ xs: 'auto', md: 0 }}>
          <DownloadCSVModalOptions
            showRainfall={showRainfall}
            setShowRainfall={setShowRainfall}
            showWaterLevel={showWaterLevel}
            setShowWaterLevel={setShowWaterLevel}
            showFilteredWaterLevel={showFilteredWaterLevel}
            setShowFilteredWaterLevel={setShowFilteredWaterLevel}
            fileType={fileType}
            setFileType={setFileType}
          />
        </Stack>
      </MHMDialog.Content>
      <DialogActions>
        {loading ? (
          <MHMLoadingIcon size={25} />
        ) : (
          <MHMButton
            color="primary"
            disabled={!isValid}
            onClick={handleDownload}
          >
            {t('Download')}
          </MHMButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DownloadCSVModal;
